import { FC, DragEvent, useState, useEffect } from 'react';
import cx from 'classnames/bind';
import { motion, AnimatePresence } from 'framer-motion';

import { Layout } from '@/components/Layout';
import { Info } from '@/components/Info';
import { Notification } from '@/components/Notification';
import { Link } from '@/components/Link';
import { Packs } from '@/components/Training/Packs';
import type { IMiniGame } from '@/types/miniGames';

import styles from './Step2.module.scss';

interface Step2Props {
  setStep: (value: number) => void;
  userToken: string;
}

const cn = cx.bind(styles);

const Step2: FC<Step2Props> = ({ setStep, userToken }) => {
  const [isRightAnswer, setIsRightAnswer] = useState(false);
  const [isShake, setIsShake] = useState(false);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [srcImage, setSrcImage] = useState('');
  const [miniGames, setMiniGames] = useState<IMiniGame[]>();
  const [currentQuestion, setCurrentQuestion] = useState<IMiniGame>();

  useEffect(() => {
    fetch(`https://admin.znatok.jti-games.dev.familyagency.ru/api/mini-game`)
      .then((response) => response.json())
      .then(({ data }) => setMiniGames(data));
  }, []);

  useEffect(() => {
    if (miniGames?.[questionNumber]) {
      setCurrentQuestion(miniGames[questionNumber]);
    }
  }, [miniGames, questionNumber]);

  const startGame = () => {
    setStep(3);

    fetch(`https://admin.znatok.jti-games.dev.familyagency.ru/api/mini-game/update`, {
      method: 'POST',
      headers: {
        UserToken: userToken,
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        miniGameStatus: false,
      }),
    }).then((response) => response.json());
  };

  const dragOverHandler = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const dropHandler = (e: DragEvent<HTMLDivElement>) => {
    const correctAnswerId = e.dataTransfer.getData('correctAnswerId');
    const isRight = currentQuestion?.id === Number(correctAnswerId);

    if (isRight) {
      const card = document.getElementById(correctAnswerId)!;
      const src = card.children[0].getAttribute('src')!;
      setSrcImage(src);
      card.style.display = 'none';
    }

    if (!isRight) {
      setIsShake(true);
    }

    if (isRight !== undefined) {
      setIsRightAnswer(isRight);
    }
  };

  useEffect(() => {
    if (miniGames?.length === questionNumber) {
      startGame();
    }
  }, [miniGames, questionNumber]);

  const MotionNotification = motion(Notification);

  return (
    <Layout className={cn('container')}>
      {isRightAnswer && (
        <AnimatePresence>
          <MotionNotification
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 2, delay: 0.3, ease: 'backInOut' }}
            onAnimationComplete={() => {
              setIsRightAnswer(false);
              setQuestionNumber((prev) => prev + 1);
            }}
            className={cn('points')}
            text="Верно!"
          />
        </AnimatePresence>
      )}
      <div className={cn('wrapper')}>
        <Info>
          <Link
            href="https://www.jti-partner.ru/products"
            text="Узнать о продукции"
          />
          <p className={cn('description')}>
            Верно совместите <span>пачку</span> и <span>название</span>, чтобы
            перейти к сборке заказов
          </p>
        </Info>
        {currentQuestion?.text && (
          <AnimatePresence>
            <motion.h1
              key={questionNumber}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ position: 'absolute', bottom: '0', opacity: 0 }}
              transition={{ duration: 1, ease: 'backInOut' }}
              className={cn('title')}
            >
              {currentQuestion.text}
            </motion.h1>
          </AnimatePresence>
        )}
      </div>
      <div
        className={cn(
          'field',
          isRightAnswer ? 'field-correct' : '',
          isShake ? 'field-incorrect' : '',
        )}
        onDragOver={(e) => dragOverHandler(e)}
        onDrop={(e) => dropHandler(e)}
      >
        Перетащите выбранную пачку в это поле
        {isRightAnswer && (
          <div className={cn('item')}>
            <img className={cn('img')} src={srcImage} alt="Упаковка сигарет" />
          </div>
        )}
      </div>
      <Packs
        setStep={setStep}
        questionNumber={questionNumber}
        setIsShake={setIsShake}
        currentQuestion={currentQuestion}
        miniGames={miniGames}
      />
    </Layout>
  );
};

export default Step2;
